import React, { useEffect } from 'react';
import { User } from 'app/constants/schemas/User';
import { getAuthUser } from 'app/models/auth/selectors';
import FluentdLogger from 'app/services/fluentdLogger';
import {
  getLocalClientVersion,
  getLocalClientRelease,
  getLocalBreakingChangeTimestamp,
} from 'app/utils/clientVersion';
import {
  useSessionStorage,
  getItem,
  setItem,
} from 'app/utils/StorageContext';
import { connect } from 'react-redux';
import { createGuid } from 'app/types/guid';

export const SESSION_ID = 'SESSION_ID';

interface IProps {
  user: User;
  generateTestSessionId?: () => string;
}

/**
 * Updates the FluentdLogger context on mount and when the user or session id change.
 */
const FluentdContextUpdater: React.FunctionComponent<IProps> = ({ user, generateTestSessionId }) => {
  const storage = useSessionStorage();

  useEffect(() => {
    FluentdLogger.setContext('client_version', getLocalClientVersion());
    FluentdLogger.setContext('client_release', getLocalClientRelease());
    FluentdLogger.setContext('breaking_change_timestamp', getLocalBreakingChangeTimestamp());
  }, []);

  const userId = (user && user.id) || undefined;
  useEffect(() => {
    FluentdLogger.setContext('user_id', userId);
  }, [userId]);

  const sessionId = getItem(storage, SESSION_ID);
  useEffect(() => {
    if (!sessionId) {
      const newSessionId = generateTestSessionId ? generateTestSessionId() : createGuid();
      setItem(storage, SESSION_ID, newSessionId);
    }
  }, [sessionId, generateTestSessionId, storage]);

  useEffect(() => {
    if (sessionId) {
      FluentdLogger.setContext('session_id', sessionId);
    }
  }, [sessionId]);

  return null;
};

const ConnectedFluentdContextUpdater = connect(
  (state) => ({
    user: getAuthUser(state),
  }),
)(FluentdContextUpdater);

export default ConnectedFluentdContextUpdater;
